// libraries
import { doc, getDoc } from "firebase/firestore";
import {
  UploadTaskSnapshot,
  StorageReference,
  ref as stgRef,
  uploadBytesResumable,
  getDownloadURL,
  FirebaseStorage,
} from "firebase/storage";

// services
import { firebaseRegionStorage, firestoreRegionDb } from "./firebase.init";
import { getGlobalUserAccessKey } from "wombat-global/src/utilities";
import {
  getTargetFromStoragePath,
  storagePath,
  TARGET_META_COLLECTION,
} from "wombat-global/src/constants";

// types
import {
  IClientTarget,
  ISiteTarget,
  ITenantTarget,
} from "wombat-global/src/typings";
import { TargetMetaDocument } from "wombat-global/src/typings/multi-region";

function joinFilePath(...paths: (string | undefined)[]): string {
  return paths
    .reduce<string[]>((res, path) => {
      if (path) {
        path.split("/").forEach((x) => {
          res.push(x);
        });
      }
      return res;
    }, [])
    .filter((x) => x && x != "")
    .join("/");
}
class FirebaseStorageUtil {
  /**
   * cache what files have been successfully uploaded (file names)
   */
  uploadCache: Record<string, UploadTaskSnapshot> = {};

  /**
   * firestore storage utilities
   */
  ref = stgRef;
  getDownloadURL = getDownloadURL;

  getRegionFromRef(
    ref?: string,
  ): IClientTarget | ISiteTarget | ITenantTarget | "us" | "ca" | undefined {
    if (!ref) {
      return "us";
    }

    if (!ref?.startsWith("gs://")) {
      return getTargetFromStoragePath(ref);
    }

    const usBucket = firebaseRegionStorage.us.app.options.storageBucket;
    const caBucket = firebaseRegionStorage.ca.app.options.storageBucket;
    if (ref.startsWith(`gs://${usBucket}`) || usBucket === caBucket) {
      return "us";
    }
    return "ca";
  }

  async getStorageRef(
    target?: IClientTarget | ISiteTarget | ITenantTarget | "us" | "ca",
    path?: string,
  ): Promise<StorageReference> {
    const _storageBucket = await this.getStorage(target);

    let _path = path;
    if (typeof target == "object" && !_path?.startsWith(storagePath(target))) {
      _path = joinFilePath(storagePath(target), path);
    }
    return stgRef(_storageBucket, _path);
  }

  async getStorage(
    target?: IClientTarget | ISiteTarget | ITenantTarget | "us" | "ca",
  ): Promise<FirebaseStorage> {
    if (!target) {
      return firebaseRegionStorage.us;
    }

    if (typeof target === "string") {
      return firebaseRegionStorage[target] || firebaseRegionStorage.us;
    }

    const targetMeta = await getDoc(
      doc(
        firestoreRegionDb.us,
        [TARGET_META_COLLECTION, getGlobalUserAccessKey(target)].join("/"),
      ),
    ).then((d) => d.data() as TargetMetaDocument | undefined);

    return (
      firebaseRegionStorage[targetMeta?.local || "us"] ||
      firebaseRegionStorage.us
    );
  }

  /**
   * generate storage paths from key parameters
   */
  // path(target: IClientTarget): string {
  //   return storagePath(target);
  // }

  put(
    ref: Promise<StorageReference> | StorageReference,
    file: File,
    onNext: (ev: UploadTaskSnapshot) => void,
  ): Promise<UploadTaskSnapshot> {
    return file
      .arrayBuffer()
      .then((arrayBuffer) => {
        return this.putArrayBuffer(
          Promise.resolve(ref),
          { name: file.name, type: file.type },
          arrayBuffer,
          onNext,
        );
      })
      .catch((err) => {
        // oxlint-disable-next-line no-console
        console.error(err);
        throw err;
      });
  }

  async putArrayBuffer(
    ref: Promise<StorageReference>,
    meta: {
      name: string;
      type: string;
    },
    arrayBuffer: ArrayBuffer,
    onNext: (ev: UploadTaskSnapshot) => void,
  ): Promise<UploadTaskSnapshot> {
    if (this.uploadCache[meta.name]) {
      return Promise.resolve(this.uploadCache[meta.name]);
    }

    const _ref = await ref;

    return new Promise((res, rej) => {
      const task = uploadBytesResumable(_ref, arrayBuffer, {
        contentType: meta.type,
        customMetadata: {
          name: meta.name,
        },
      });
      task.on("state_changed", (snapshot) => {
        this.uploadCache[meta.name] = snapshot;
        onNext(snapshot);
      });
      task.catch(rej);
      task.then((snapshot) => {
        this.uploadCache[meta.name] = snapshot;
        res(this.uploadCache[meta.name]);
      });
    });
  }

  clearCache(fileName?: string) {
    if (fileName !== undefined) {
      delete this.uploadCache[fileName];
      return;
    }
    this.uploadCache = {};
  }
}

export default new FirebaseStorageUtil();
export const firebaseStorageService = firebaseRegionStorage.us;
